import {
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  Maximize,
  Save,
  FolderOpen,
  FilePlus,
  Cloud,
  X,
  Grid3x3,
  Scissors,
  Copy,
  ClipboardPaste,
  HelpCircle,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';

interface EditorToolbarProps {
  zoom: number;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFitToScreen: () => void;
  /** File-menu actions, surfaced via the folder-icon dropdown. */
  onNew: () => void;
  onSave: () => void;
  onSaveAs: () => void;
  onOpenFile: () => void;
  onOpenFias: () => void;
  onCloseFile: () => void;
  /** True when there is an active tab (most file actions are disabled otherwise). */
  hasActiveFile: boolean;
  /** Edit-menu actions surfaced as toolbar icons (mobile-friendly equivalents
   *  of Ctrl+X / Ctrl+C / Ctrl+V). */
  onCut: () => void;
  onCopy: () => void;
  onPaste: () => void;
  canCutCopy: boolean;
  canPaste: boolean;
  gridVisible: boolean;
  onToggleGrid: () => void;
  /** Open the icon-legend / help dialog (top-right `?` button). */
  onShowHelp: () => void;
}

export function EditorToolbar({
  zoom,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onZoomIn,
  onZoomOut,
  onFitToScreen,
  onNew,
  onSave,
  onSaveAs,
  onOpenFile,
  onOpenFias,
  onCloseFile,
  hasActiveFile,
  onCut,
  onCopy,
  onPaste,
  canCutCopy,
  canPaste,
  gridVisible,
  onToggleGrid,
  onShowHelp,
}: EditorToolbarProps) {
  return (
    <div className="flex items-center h-12 bg-background border-b border-border px-2 gap-1">
      {/* File menu — folder icon expands to New / Save / Save As / Open / Close. */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="p-2 rounded-md hover:bg-accent text-muted-foreground"
            title="File"
            aria-label="File menu"
          >
            <FolderOpen className="h-4 w-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-48">
          <DropdownMenuItem onClick={onNew}>
            <FilePlus className="h-4 w-4 mr-2" />
            New
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onOpenFile}>
            <FolderOpen className="h-4 w-4 mr-2" />
            Open Local&hellip;
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onOpenFias}>
            <Cloud className="h-4 w-4 mr-2" />
            Open Fias&hellip;
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onSave} disabled={!hasActiveFile}>
            <Save className="h-4 w-4 mr-2" />
            Save
          </DropdownMenuItem>
          <DropdownMenuItem onClick={onSaveAs} disabled={!hasActiveFile}>
            <Save className="h-4 w-4 mr-2" />
            Save As&hellip;
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onCloseFile} disabled={!hasActiveFile}>
            <X className="h-4 w-4 mr-2" />
            Close
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <div className="w-px h-6 bg-border mx-1" />

      {/* Undo/Redo */}
      <ToolbarButton icon={Undo2} title="Undo (Ctrl+Z)" onClick={onUndo} disabled={!canUndo} />
      <ToolbarButton
        icon={Redo2}
        title="Redo (Ctrl+Shift+Z)"
        onClick={onRedo}
        disabled={!canRedo}
      />

      <div className="w-px h-6 bg-border mx-1" />

      {/* Edit — Cut / Copy / Paste. Cut & Copy require an active marquee;
          Paste requires non-empty in-memory clipboard. */}
      <ToolbarButton icon={Scissors} title="Cut (Ctrl+X)" onClick={onCut} disabled={!canCutCopy} />
      <ToolbarButton icon={Copy} title="Copy (Ctrl+C)" onClick={onCopy} disabled={!canCutCopy} />
      <ToolbarButton
        icon={ClipboardPaste}
        title="Paste (Ctrl+V)"
        onClick={onPaste}
        disabled={!canPaste}
      />

      <div className="w-px h-6 bg-border mx-1" />

      {/* Zoom */}
      <ToolbarButton icon={ZoomOut} title="Zoom out" onClick={onZoomOut} />
      <span className="text-xs text-muted-foreground w-12 text-center tabular-nums">
        {Math.round(zoom * 100)}%
      </span>
      <ToolbarButton icon={ZoomIn} title="Zoom in" onClick={onZoomIn} />
      <ToolbarButton icon={Maximize} title="Fit to screen" onClick={onFitToScreen} />

      <div className="w-px h-6 bg-border mx-1" />

      {/* Grid toggle */}
      <button
        type="button"
        className={`p-2 rounded-md transition-colors ${
          gridVisible ? 'bg-accent text-foreground' : 'text-muted-foreground hover:bg-accent'
        }`}
        title="Toggle transparency grid"
        onClick={onToggleGrid}
      >
        <Grid3x3 className="h-4 w-4" />
      </button>

      {/* Help / icon legend — right-aligned. Especially useful on mobile
          where users can't hover for tooltips. */}
      <button
        type="button"
        className="ml-auto p-2 rounded-md text-muted-foreground hover:bg-accent hover:text-foreground"
        title="Show icon reference"
        aria-label="Show icon reference"
        onClick={onShowHelp}
      >
        <HelpCircle className="h-4 w-4" />
      </button>
    </div>
  );
}

function ToolbarButton({
  icon: Icon,
  title,
  onClick,
  disabled,
}: {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      className="p-2 rounded-md hover:bg-accent text-muted-foreground disabled:opacity-40 disabled:pointer-events-none"
      title={title}
      onClick={onClick}
      disabled={disabled}
    >
      <Icon className="h-4 w-4" />
    </button>
  );
}
